/**
 * Combo Builder - milestone tier edition
 *
 * Customers fill numbered slots. Each tier (4 / 6 / 8) is a milestone with a
 * FIXED price. Add-to-cart only unlocks on an exact milestone; between
 * milestones the panel upsells the next one.
 *
 * Pricing is enforced by adding a single hidden pack variant (e.g. "Pack of 4"
 * at Rs. 599) with the customer's picks attached as line-item properties, so
 * the price is a real Shopify product price and cannot be tampered with -
 * whatever the individual items are worth.
 *
 * On phone the combo panel sits inline and is always visible; the sticky bar
 * simply scrolls to it.
 */

var HamperConfetti = (function () {
  var canvas, ctx, particles = [], raf = null, dpr = 1;

  function ensureCanvas() {
    if (canvas) return;
    canvas = document.createElement('canvas');
    canvas.className = 'hamper-confetti-canvas';
    document.body.appendChild(canvas);
    ctx = canvas.getContext('2d');
    resize();
    window.addEventListener('resize', resize);
  }

  function resize() {
    if (!canvas) return;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    canvas.style.width = window.innerWidth + 'px';
    canvas.style.height = window.innerHeight + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function spawn(originX, originY, count, colors, angle, spread, scale) {
    for (var i = 0; i < count; i++) {
      var a = (angle + (Math.random() - 0.5) * spread) * (Math.PI / 180);
      var speed = (7 + Math.random() * 9) * scale;
      particles.push({
        x: originX, y: originY,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed,
        w: (6 + Math.random() * 5) * scale,
        h: (8 + Math.random() * 6) * scale,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.35,
        color: colors[(Math.random() * colors.length) | 0],
        life: 0,
        ttl: 90 + Math.random() * 50,
        tilt: Math.random() * Math.PI
      });
    }
  }

  function frame() {
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

    for (var i = particles.length - 1; i >= 0; i--) {
      var p = particles[i];
      p.life++;
      p.vy += 0.28;
      p.vx *= 0.99;
      p.vy *= 0.99;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      p.tilt += 0.1;

      var alpha = p.life > p.ttl * 0.65
        ? Math.max(0, 1 - (p.life - p.ttl * 0.65) / (p.ttl * 0.35))
        : 1;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.scale(Math.cos(p.tilt), 1);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();

      if (p.life > p.ttl || p.y > window.innerHeight + 40) particles.splice(i, 1);
    }

    if (particles.length) {
      raf = requestAnimationFrame(frame);
    } else {
      raf = null;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    }
  }

  return {
    burst: function (el, colors) {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      ensureCanvas();

      var narrow = window.innerWidth < 600;
      var scale = narrow ? 0.78 : 1;
      var spreadX = narrow ? 55 : 90;

      var cx = window.innerWidth / 2;
      var cy = window.innerHeight / 2;
      if (el) {
        var r = el.getBoundingClientRect();
        cx = r.left + r.width / 2;
        cy = Math.max(70, Math.min(r.top + r.height / 2, window.innerHeight - 70));
      }

      spawn(cx - spreadX, cy, narrow ? 24 : 34, colors, -70, 55, scale);
      spawn(cx + spreadX, cy, narrow ? 24 : 34, colors, -110, 55, scale);
      spawn(cx, cy, narrow ? 18 : 26, colors, -90, 80, scale);

      if (!raf) raf = requestAnimationFrame(frame);
    }
  };
})();

customElements.get('hamper-builder') ||
  customElements.define(
    'hamper-builder',
    class HamperBuilder extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;

        var d = this.dataset;
        this.sectionId = d.sectionId || 'default';
        this.cartAddUrl = d.cartAddUrl || '/cart/add.js';
        this.cartUrl = d.cartUrl || '/cart';
        this.allowDuplicates = d.allowDuplicates !== 'false';
        this.freeShippingEnabled = d.freeShippingEnabled === 'true';
        this.freeShippingThreshold = parseInt(d.freeShipping || '0', 10);
        this.maxSize = parseInt(d.maxSize || '0', 10);
        this.celebrateEnabled = d.celebrate !== 'false';
        this.storageKey = 'hamper:' + this.sectionId;

        var sizes = (d.sizes || '').split(',').filter(Boolean);
        var ids = (d.packIds || '').split(',').filter(Boolean);
        var prices = (d.packPrices || '').split(',').filter(Boolean);
        var labels = (d.packLabels || '').split('~').filter(Boolean);

        this.tiers = sizes.map(function (s, i) {
          return {
            size: parseInt(s, 10),
            variantId: ids[i],
            price: parseInt(prices[i], 10),
            label: labels[i] || s + ' Pack'
          };
        }).sort(function (a, b) { return a.size - b.size; });

        if (!this.tiers.length) return;

        this.cards = Array.from(this.querySelectorAll('.hamper-card'));
        this.slotEls = Array.from(this.querySelectorAll('.hamper-slot'));
        this.tabs = Array.from(this.querySelectorAll('.hamper-tab'));
        this.meterFills = Array.from(this.querySelectorAll('[data-meter-fill]'));
        this.meterStops = Array.from(this.querySelectorAll('[data-meter-stop]'));
        this.searchEl = this.querySelector('.hamper-builder__search');
        this.noResultsEl = this.querySelector('[data-no-results]');
        this.hintEl = this.querySelector('[data-hint]');
        this.savingsRow = this.querySelector('[data-savings-row]');
        this.savingsEl = this.querySelector('[data-savings]');
        this.savingsPctEl = this.querySelector('[data-savings-pct]');
        this.mrpEl = this.querySelector('[data-mrp]');
        this.shippingTextEl = this.querySelector('[data-shipping-text]');
        this.errorWrapper = this.querySelector('[data-error-wrapper]');
        this.errorMessage = this.querySelector('[data-error-message]');
        this.clearBtn = this.querySelector('[data-clear]');
        this.summary = this.querySelector('[data-summary]');
        this.mobileBar = this.querySelector('[data-mobile-bar]');
        this.bundleQtyValue = this.querySelector('[data-bundle-qty-value]');
        this.totalValueEl = this.querySelector('.hamper-bundle__total-value');

        this.addToCartButtons = Array.from(this.querySelectorAll('[data-add-to-cart]'));
        this.packPriceEls = Array.from(this.querySelectorAll('[data-pack-price]'));
        this.filledEls = Array.from(this.querySelectorAll('[data-filled]'));
        this.targetEls = Array.from(this.querySelectorAll('[data-target]'));

        this.selection = [];
        this.bundleQty = 1;
        this.lastCelebrated = null;

        this.confettiColors = ['#E4B23F', '#C0392B', '#7B1E1E', '#F3C57F', '#2E7D32', '#FFF3D6'];

        this.bindEvents();
        this.restore();
        var restored = this.currentTier();
        this.lastCelebrated = restored ? restored.size : null;
        this.render();
      }

      currentTier() {
        var n = this.selection.length;
        return this.tiers.find(function (t) { return t.size === n; }) || null;
      }

      nextTier() {
        var n = this.selection.length;
        return this.tiers.find(function (t) { return t.size > n; }) || null;
      }

      displayTier() {
        return this.currentTier() || this.nextTier() || this.tiers[this.tiers.length - 1];
      }

      bindEvents() {
        var self = this;

        this.cards.forEach(function (card) {
          var actions = card.querySelector('.hamper-card__actions');
          if (!actions) return;
          actions.addEventListener('click', function (event) {
            var btn = event.target.closest('[data-action]');
            if (!btn) return;
            var a = btn.dataset.action;
            if (a === 'add' || a === 'increase') self.addItem(actions);
            if (a === 'decrease') self.removeItem(actions.dataset.variantId);
          });
        });

        this.tabs.forEach(function (tab) {
          tab.addEventListener('click', function () { self.selectTab(tab); });
        });

        if (this.searchEl) {
          this.searchEl.addEventListener('input', function () { self.applyFilters(); });
        }

        this.addToCartButtons.forEach(function (btn) {
          btn.addEventListener('click', function () { self.handleAddToCart(); });
        });

        this.querySelectorAll('[data-bundle-qty]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var dir = btn.dataset.bundleQty;
            self.bundleQty = Math.max(1, Math.min(20, self.bundleQty + (dir === 'up' ? 1 : -1)));
            if (self.bundleQtyValue) self.bundleQtyValue.textContent = self.bundleQty;
            self.render();
          });
        });

        if (this.clearBtn) {
          this.clearBtn.addEventListener('click', function () {
            self.selection = [];
            self.lastCelebrated = null;
            self.persist();
            self.render();
          });
        }

        var openBtn = this.querySelector('[data-open-sheet]');
        if (openBtn) {
          openBtn.addEventListener('click', function () { self.revealSummary(); });
        }

        this.slotEls.forEach(function (slot) {
          slot.addEventListener('click', function (event) {
            if (!event.target.closest('[data-slot-remove]')) return;
            var idx = parseInt(slot.dataset.slot, 10) - 1;
            self.removeAt(idx);
          });
        });
      }

      revealSummary() {
        if (!this.summary) return;
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.summary.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }

      selectTab(tab) {
        this.tabs.forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        this.activeCategory = tab.dataset.category;
        this.applyFilters();
      }

      applyFilters() {
        var cat = this.activeCategory || '__all';
        var q = this.searchEl ? this.searchEl.value.trim().toLowerCase() : '';
        var visible = 0;

        this.cards.forEach(function (card) {
          var okCat = cat === '__all' || card.dataset.category === cat;
          var okQ = !q || (card.dataset.search || '').indexOf(q) !== -1;
          var show = okCat && okQ;
          card.hidden = !show;
          if (show) visible++;
        });

        if (this.noResultsEl) this.noResultsEl.hidden = visible !== 0;
      }

      addItem(actions) {
        if (this.selection.length >= this.maxSize) return;
        if (actions.dataset.available === 'false') return;

        var variantId = actions.dataset.variantId;
        if (!this.allowDuplicates && this.selection.some(function (p) { return p.variantId === variantId; })) return;

        this.selection.push({
          variantId: variantId,
          title: actions.dataset.title,
          image: actions.dataset.imageSrc,
          price: parseInt(actions.dataset.price, 10) || 0
        });

        this.hideError();
        this.persist();
        this.render();
      }

      removeItem(variantId) {
        var i = this.selection.map(function (p) { return p.variantId; }).lastIndexOf(variantId);
        if (i > -1) this.selection.splice(i, 1);
        this.persist();
        this.render();
      }

      removeAt(index) {
        if (index < 0 || index >= this.selection.length) return;
        this.selection.splice(index, 1);
        this.persist();
        this.render();
      }

      counts() {
        return this.selection.reduce(function (acc, p) {
          acc[p.variantId] = (acc[p.variantId] || 0) + 1;
          return acc;
        }, {});
      }

      persist() {
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify({ selection: this.selection }));
        } catch (e) { /* private mode */ }
      }

      restore() {
        var saved;
        try {
          saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        } catch (e) { return; }
        if (!saved || !Array.isArray(saved.selection)) return;

        var valid = {};
        this.cards.forEach(function (c) {
          var a = c.querySelector('.hamper-card__actions');
          if (a && a.dataset.available !== 'false') valid[a.dataset.variantId] = true;
        });

        this.selection = saved.selection
          .filter(function (p) { return valid[String(p.variantId)]; })
          .slice(0, this.maxSize);
      }

      render() {
        var self = this;
        var filled = this.selection.length;
        var current = this.currentTier();
        var next = this.nextTier();
        var display = this.displayTier();

        this.renderSlots(filled);
        this.renderCards();
        this.renderMeter(filled);

        this.filledEls.forEach(function (el) { el.textContent = filled; });
        this.targetEls.forEach(function (el) { el.textContent = display ? display.size : ''; });

        var unit = 0;
        if (filled > 0) unit = current ? current.price : (display ? display.price : 0);

        this.packPriceEls.forEach(function (el) {
          el.textContent = self.formatMoney(unit * self.bundleQty);
          el.style.opacity = (filled === 0 || current) ? '' : '0.4';
        });

        this.renderSavings(current, unit);

        if (this.hintEl) {
          this.hintEl.textContent = this.hintText(filled, current, next);
          this.hintEl.classList.toggle('is-ready', !!current);
        }

        this.addToCartButtons.forEach(function (btn) { btn.disabled = !current; });
        if (this.clearBtn) this.clearBtn.hidden = filled === 0;
        this.classList.toggle('is-complete', !!current);
        if (this.mobileBar) this.mobileBar.classList.toggle('is-active', filled > 0);

        if (this.freeShippingEnabled) this.renderShipping(current ? current.price * this.bundleQty : 0);

        this.maybeCelebrate(current);
      }

      maybeCelebrate(current) {
        if (!current) {
          if (this.lastCelebrated !== null && this.selection.length < this.lastCelebrated) {
            this.lastCelebrated = null;
          }
          return;
        }
        if (this.lastCelebrated === current.size) return;

        this.lastCelebrated = current.size;

        if (this.celebrateEnabled) {
          var anchor = this.totalValueEl || this.querySelector('.hamper-panel--bundle');
          HamperConfetti.burst(anchor, this.confettiColors);
        }

        var panel = this.querySelector('.hamper-panel--bundle');
        if (panel) {
          panel.classList.remove('is-celebrating');
          void panel.offsetWidth;
          panel.classList.add('is-celebrating');
        }
      }

      renderMeter(filled) {
        var pct = this.maxSize ? Math.min((filled / this.maxSize) * 100, 100) : 0;
        this.meterFills.forEach(function (el) { el.style.width = pct + '%'; });

        this.meterStops.forEach(function (stop) {
          var size = parseInt(stop.dataset.meterStop, 10);
          stop.classList.toggle('is-reached', filled >= size);
          stop.classList.toggle('is-current', filled === size);
        });
      }

      renderSavings(current, unit) {
        if (!this.savingsRow) return;

        var mrp = this.selection.reduce(function (s, p) { return s + p.price; }, 0);
        var save = mrp - unit;

        if (this.mrpEl) {
          if (current && save > 0) {
            this.mrpEl.hidden = false;
            this.mrpEl.textContent = this.formatMoney(mrp * this.bundleQty);
          } else {
            this.mrpEl.hidden = true;
          }
        }

        if (current && save > 0) {
          this.savingsRow.hidden = false;
          this.savingsEl.textContent = this.formatMoney(save * this.bundleQty);
          if (this.savingsPctEl) {
            var pct = Math.round((save / mrp) * 100);
            this.savingsPctEl.textContent = pct > 0 ? ' (' + pct + '% off)' : '';
          }
        } else {
          this.savingsRow.hidden = true;
        }
      }

      hintText(filled, current, next) {
        if (filled === 0) {
          var t = this.tiers[0];
          return 'Add ' + t.size + ' items for ' + this.formatMoney(t.price) + '.';
        }
        if (current && !next) return 'Your ' + current.label + ' is ready!';
        if (current && next) {
          var more = next.size - filled;
          return current.label + ' ready. Add ' + more + ' more for the ' +
            next.label + ' at ' + this.formatMoney(next.price) + '.';
        }
        if (next) {
          var left = next.size - filled;
          return 'Add ' + left + ' more item' + (left === 1 ? '' : 's') +
            ' for your ' + next.label + ' at ' + this.formatMoney(next.price) + '.';
        }
        return '';
      }

      renderSlots(filled) {
        var self = this;
        this.slotEls.forEach(function (slot) {
          var num = parseInt(slot.dataset.slot, 10);
          var idx = num - 1;
          var product = self.selection[idx];

          if (product) {
            if (slot.dataset.fillId !== product.variantId + ':' + idx) {
              slot.dataset.fillId = product.variantId + ':' + idx;
              var media = product.image
                ? '<img class="hamper-slot__img" src="' + product.image + '" alt="" loading="lazy">'
                : '<span class="hamper-slot__initials">' + self.escape(product.title.slice(0, 2).toUpperCase()) + '</span>';
              slot.innerHTML = media +
                '<button type="button" class="hamper-slot__remove" data-slot-remove ' +
                'aria-label="Remove ' + self.escape(product.title) + '">' +
                '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 2 L10 10 M10 2 L2 10" ' +
                'stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg></button>';
            }
            slot.classList.add('is-filled');
            slot.title = product.title;
          } else {
            if (slot.dataset.fillId) {
              slot.dataset.fillId = '';
              slot.innerHTML = '<span class="hamper-slot__num">' + num + '</span>';
            }
            slot.classList.remove('is-filled');
            slot.removeAttribute('title');
          }

          if (slot.classList.contains('is-milestone')) {
            slot.classList.toggle('is-goal-hit', filled >= num);
          }
        });
      }

      renderCards() {
        var counts = this.counts();
        var full = this.selection.length >= this.maxSize;
        var self = this;

        this.cards.forEach(function (card) {
          var actions = card.querySelector('.hamper-card__actions');
          if (!actions || actions.dataset.available === 'false') return;

          var count = counts[actions.dataset.variantId] || 0;
          var addBtn = actions.querySelector('[data-action="add"]');
          var stepper = actions.querySelector('[data-stepper]');
          var countEl = actions.querySelector('[data-count]');
          var incBtn = actions.querySelector('[data-action="increase"]');

          card.classList.toggle('is-selected', count > 0);

          if (count > 0) {
            if (addBtn) addBtn.hidden = true;
            if (stepper) stepper.hidden = false;
            if (countEl) countEl.textContent = count;
            if (incBtn) incBtn.disabled = full || !self.allowDuplicates;
          } else {
            if (addBtn) { addBtn.hidden = false; addBtn.disabled = full; }
            if (stepper) stepper.hidden = true;
          }
        });
      }

      renderShipping(totalPaise) {
        if (!this.shippingTextEl || !this.freeShippingThreshold) return;
        if (totalPaise >= this.freeShippingThreshold) {
          this.shippingTextEl.innerHTML = '<strong>Free shipping unlocked!</strong>';
          this.shippingTextEl.classList.add('is-won');
        } else {
          this.shippingTextEl.innerHTML =
            'Shop for <strong>' + this.formatMoney(this.freeShippingThreshold) +
            '</strong> or more and shipping is on us!';
          this.shippingTextEl.classList.remove('is-won');
        }
      }

      handleAddToCart() {
        var tier = this.currentTier();
        if (!tier) return;

        var self = this;
        this.setLoading(true);
        this.hideError();

        var counts = this.counts();
        var seen = {};
        var readable = this.selection
          .filter(function (p) {
            if (seen[p.variantId]) return false;
            seen[p.variantId] = true;
            return true;
          })
          .map(function (p) {
            return counts[p.variantId] > 1 ? p.title + ' x ' + counts[p.variantId] : p.title;
          })
          .join(', ');

        var properties = {
          'Your selection': readable,
          _hamper_items: JSON.stringify(
            Object.keys(counts).map(function (id) { return { variant_id: id, qty: counts[id] }; })
          ),
          _hamper_pack: tier.label
        };

        fetch(this.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: tier.variantId, quantity: this.bundleQty, properties: properties }]
          })
        })
          .then(function (res) { return res.json(); })
          .then(function (response) {
            if (response.status) {
              self.showError(response.description || 'Something went wrong adding your combo to the cart.');
              return;
            }
            self.selection = [];
            self.bundleQty = 1;
            self.lastCelebrated = null;
            if (self.bundleQtyValue) self.bundleQtyValue.textContent = '1';
            self.persist();
            self.render();
            document.dispatchEvent(new CustomEvent('hamper-builder:added', { bubbles: true, detail: response }));
            self.openCart();
          })
          .catch(function () {
            self.showError('Something went wrong adding your combo to the cart. Please try again.');
          })
          .finally(function () { self.setLoading(false); });
      }

      openCart() {
        var drawer = document.querySelector('cart-drawer, cart-notification, [data-cart-drawer]');
        if (!drawer || typeof drawer.open !== 'function') {
          window.location.href = this.cartUrl;
          return;
        }
        drawer.open();
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      setLoading(loading) {
        var self = this;
        this.addToCartButtons.forEach(function (btn) {
          btn.classList.toggle('loading', loading);
          btn.setAttribute('aria-busy', loading ? 'true' : 'false');
          btn.disabled = loading ? true : !self.currentTier();
        });
      }

      showError(message) {
        if (!this.errorWrapper) return;
        this.errorWrapper.hidden = false;
        this.errorMessage.textContent = message;
      }

      hideError() {
        if (this.errorWrapper) this.errorWrapper.hidden = true;
      }

      escape(str) {
        var map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
        return String(str).replace(/[&<>"']/g, function (c) { return map[c]; });
      }

      formatMoney(paise) {
        var amount = paise / 100;
        var hasDecimals = Math.round(amount * 100) % 100 !== 0;
        return 'Rs. ' + new Intl.NumberFormat('en-IN', {
          minimumFractionDigits: hasDecimals ? 2 : 0,
          maximumFractionDigits: 2
        }).format(amount);
      }
    }
  );
